import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { BackButton } from "@/components/BackButton";
import { api } from "@/lib/api";
import { Search, Plus, Eye, MapPin, Tag } from "lucide-react";
import { Seo } from "@/components/Seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const mediaUrl = (u) => (u ? `${BACKEND}${u}` : null);

export default function Marketplace() {
  const [ads, setAds] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (cat) params.set("category", cat);
      params.set("sort", sort);
      const { data } = await api.get(`/ads?${params.toString()}`);
      setAds(data.ads);
      setCats(data.categories);
    } finally { setLoading(false); }
  }, [q, cat, sort]);

  useEffect(() => { load(); }, [cat, sort]);

  return (
    <div className="min-h-screen bg-[#0A0A14] text-slate-200">
      <Seo
        title="Marketplace — Discover & Promote Offers | InnerArcade"
        description="Browse and promote ads and local offers on the InnerArcade marketplace. Discover deals from restaurants, events, wellness and more."
        path="/market"
        keywords="advertising marketplace, promote your business, local offers, sponsored ads, discover deals online"
      />
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <BackButton className="mb-6" />
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-sm tracking-[0.3em] uppercase text-violet-300/80 mb-2">Marketplace</p>
            <h1 className="font-head font-900 text-4xl text-slate-50">EXPLORE ADS</h1>
          </div>
          <Link to="/market/new" data-testid="post-ad-btn" className="inline-flex items-center gap-2 font-arcade text-lg px-6 py-3 bg-[#A78BFA] text-black border border-[#A78BFA] hover:-translate-y-0.5 transition-transform duration-200">
            <Plus className="w-5 h-5" /> POST AN AD
          </Link>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 mb-5">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
            <input data-testid="ad-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ads…"
              className="w-full rounded-xl bg-white/5 border border-white/10 pl-10 pr-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-[#A78BFA]/50 outline-none transition-colors duration-300" />
          </div>
          <select data-testid="ad-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-xl bg-white/5 border border-white/10 px-3 text-sm text-slate-200 outline-none">
            <option value="recent">Recent</option>
            <option value="popular">Popular</option>
          </select>
        </form>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="ad-categories">
          <button onClick={() => setCat("")} className={`px-3 py-1.5 rounded-full text-xs border transition-colors duration-200 ${cat === "" ? "bg-[#A78BFA] text-black border-[#A78BFA]" : "text-slate-300 border-white/15 hover:border-white/40"}`}>All</button>
          {cats.map((c) => (
            <button key={c} data-testid={`cat-${c}`} onClick={() => setCat(c)} className={`px-3 py-1.5 rounded-full text-xs border transition-colors duration-200 ${cat === c ? "bg-[#A78BFA] text-black border-[#A78BFA]" : "text-slate-300 border-white/15 hover:border-white/40"}`}>{c}</button>
          ))}
        </div>

        {loading ? (
          <p className="text-slate-500">Loading ads…</p>
        ) : ads.length === 0 ? (
          <div className="text-center py-16" data-testid="ads-empty">
            <p className="text-slate-400 mb-4">No ads yet. Be the first to post one.</p>
            <Link to="/market/new" className="font-arcade text-lg text-[#A78BFA]">POST AN AD →</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ads-grid">
            {ads.map((ad) => (
              <Link key={ad.id} to={`/market/${ad.id}`} data-testid={`ad-card-${ad.id}`} className="group rounded-2xl border border-white/10 bg-[#11111A] overflow-hidden hover:-translate-y-1 transition-transform duration-200">
                <div className="aspect-[16/10] bg-white/5 overflow-hidden">
                  {ad.image_urls?.[0] ? (
                    <img src={mediaUrl(ad.image_urls[0])} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-600"><Tag className="w-8 h-8" /></div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-wider text-[#A78BFA]">{ad.category}</span>
                    {ad.price && <span className="text-xs text-[#F5C842] ml-auto">{ad.price}</span>}
                  </div>
                  <h3 className="font-head font-700 text-lg text-slate-50 line-clamp-1">{ad.title}</h3>
                  <p className="text-sm text-slate-400 line-clamp-2 mt-1">{ad.description}</p>
                  <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                    {ad.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {ad.location}</span>}
                    <span className="inline-flex items-center gap-1 ml-auto"><Eye className="w-3 h-3" /> {ad.views}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
